/**
 * iter-261 — StemPackCard.
 *
 * Single pack tile in the marketplace browse grid. Surfaces the
 * iter-253 auto-tagged manifest fields (tempo + key + stems_model) as
 * chips so producers can size-up the pack before downloading.
 *
 * Click affordances:
 *   • Like button (gated to signed-in users — opens AuthMenu if not)
 *   • Download (open to anyone)
 *   • Delete (owner-only, hidden otherwise)
 */
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Download, Heart, Trash2, Music2, Wand2, Play, Pause, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import {
  toggleStemPackLike,
  deleteStemPack,
  downloadStemPack,
} from "@/lib/marketplaceApi";
import useMarketplacePreview from "@/components/marketplace/useMarketplacePreview";
import PackCover from "@/components/marketplace/PackCover";

function _formatTempo(bpm) {
  if (bpm == null) return null;
  return `${Math.round(bpm)} BPM`;
}

function _formatRelative(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return "";
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hr ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? "" : "s"} ago`;
  return new Date(iso).toLocaleDateString();
}

export default function StemPackCard({ pack, signedIn, onMutated, onDeleted }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // 'like' | 'download' | 'delete' | 'remix' | null

  // iter-263 — preview-before-remix: 30-second audio audition of one
  // representative stem (vocals → other → drums → first) so producers
  // can size-up a pack without committing to a full Remix load.
  const preview = useMarketplacePreview(pack);

  const tempo = _formatTempo(pack.tempo_bpm);
  const keyLabel = pack.key || (pack.key_root ? `${pack.key_root} ${pack.key_quality || ""}`.trim() : null);

  const handleLike = async () => {
    if (!signedIn) {
      toast.message("Sign in to like packs", {
        description: "Use the Sign In button at the top to start liking + publishing.",
      });
      return;
    }
    setBusy("like");
    try {
      const updated = await toggleStemPackLike(pack.pack_id);
      onMutated?.(updated);
    } catch (e) {
      toast.error("Could not toggle like", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleDownload = async () => {
    setBusy("download");
    const id = toast.loading(`Packing ${pack.stem_count} stems…`);
    try {
      const { count } = await downloadStemPack(pack.pack_id, pack.title);
      toast.dismiss(id);
      toast.success(`Downloaded ${count}-stem pack`, {
        description: `${pack.title}-pack.zip is in your Downloads folder.`,
      });
      // Optimistic: bump the local download counter so the card reflects it.
      onMutated?.({ ...pack, download_count: (pack.download_count || 0) + 1 });
    } catch (e) {
      toast.dismiss(id);
      toast.error("Could not download pack", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${pack.title}"? This cannot be undone.`)) return;
    setBusy("delete");
    try {
      await deleteStemPack(pack.pack_id);
      toast.success("Pack deleted");
      onDeleted?.(pack.pack_id);
    } catch (e) {
      toast.error("Could not delete pack", { description: e.message });
    } finally {
      setBusy(null);
    }
  };

  /**
   * Shareable permalink — copies `/marketplace/pack/<pack_id>` (origin-aware
   * so it works on preview AND production). The pack page lets anyone
   * preview each stem, remix into the studio, or download the zip.
   */
  const handleShare = async () => {
    const url = `${window.location.origin}/marketplace/pack/${encodeURIComponent(pack.pack_id)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Pack link copied", {
        description: "Anyone who opens it can preview, remix or download this pack.",
      });
    } catch (e) {
      void e;
      // Clipboard blocked (permissions / non-secure context) — surface
      // the URL so the user can copy it manually.
      toast.message("Copy this pack link", { description: url, duration: 12000 });
    }
  };

  /**
   * iter-262 — Remix entry-point. Routes to /studio?remix=<pack_id>;
   * the studio's `useRemixPack` hook reads the query param, fetches the
   * fresh pack, bumps the remix counter, and loads each stem URL into
   * a free channel via the existing /api/stems/proxy path (CORS-safe).
   * No-op if the pack has zero stems (defensive — shouldn't happen).
   */
  const handleRemix = () => {
    if (!pack.stems?.length) {
      toast.error("This pack has no stems to remix");
      return;
    }
    setBusy("remix");
    toast.message("Loading stems into your studio…", {
      description: `${pack.stem_count} channels will populate from "${pack.title}".`,
    });
    navigate(`/studio?remix=${encodeURIComponent(pack.pack_id)}`);
  };

  return (
    <article
      data-testid={`stem-pack-card-${pack.pack_id}`}
      data-preview-status={preview.status}
      className="group relative flex flex-col gap-3 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/80 via-slate-900/60 to-violet-950/40 p-5 backdrop-blur-sm transition hover:border-violet-400/40 hover:from-slate-900/90 hover:to-violet-950/60"
    >
      {/* AI cover art — click-through to the pack page */}
      <div
        data-testid="stem-pack-cover-link"
        className="-mx-5 -mt-5 block cursor-pointer"
        title="Open pack page"
        onClick={() => navigate(`/marketplace/pack/${encodeURIComponent(pack.pack_id)}`)}
      >
        <PackCover pack={pack} canGenerate={!!pack.is_owner} onUpdated={(fresh) => onMutated?.(fresh)} />
      </div>
      {/* Title row */}
      <header className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          {/* iter-263 — Preview play button. Hidden when there are
              somehow zero stems in the pack (defensive — shouldn't
              happen since backend caps at min 1). */}
          {preview.canPreview && (
            <button
              type="button"
              data-testid="stem-pack-preview-btn"
              data-status={preview.status}
              onClick={preview.toggle}
              title={
                preview.status === "playing"
                  ? `Pause preview · ${preview.previewStem.name}`
                  : preview.status === "loading"
                  ? "Loading preview…"
                  : preview.status === "error"
                  ? "Preview unavailable — tap to retry"
                  : `Preview · ${preview.previewStem.name}`
              }
              aria-label={
                preview.status === "playing" ? "Pause preview" : "Play preview"
              }
              className={
                "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ring-1 transition " +
                (preview.status === "playing"
                  ? "bg-emerald-400 text-slate-950 ring-emerald-300 shadow-lg shadow-emerald-500/30 hover:brightness-110"
                  : preview.status === "loading"
                  ? "bg-emerald-500/20 text-emerald-200 ring-emerald-400/40"
                  : preview.status === "error"
                  ? "bg-rose-500/20 text-rose-200 ring-rose-400/40 hover:bg-rose-500/30"
                  : "bg-white/5 text-emerald-200 ring-emerald-400/30 hover:bg-emerald-500/20 hover:text-emerald-100")
              }
            >
              {preview.status === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : preview.status === "playing" ? (
                <Pause className="h-4 w-4 fill-current" />
              ) : (
                <Play className="h-4 w-4 fill-current translate-x-px" />
              )}
            </button>
          )}
          <div className="min-w-0 flex-1">
            <h3
              className="truncate text-base font-semibold text-white"
              title={pack.title}
              data-testid="stem-pack-title"
            >
              <Link
                to={`/marketplace/pack/${encodeURIComponent(pack.pack_id)}`}
                data-testid="stem-pack-permalink"
                className="transition hover:text-cyan-200 hover:underline decoration-cyan-400/50 underline-offset-2"
                title="Open pack page"
              >
                {pack.title}
              </Link>
            </h3>
            <p className="mt-0.5 truncate text-xs text-slate-400">
              <span className="text-slate-300">{pack.user_name || "Anonymous"}</span>
              <span className="mx-1.5 text-slate-600">·</span>
              <span>{_formatRelative(pack.created_at)}</span>
              {preview.status === "playing" && preview.previewStem && (
                <>
                  <span className="mx-1.5 text-slate-600">·</span>
                  <span
                    className="text-emerald-300"
                    data-testid="stem-pack-preview-now"
                  >Previewing {preview.previewStem.name}</span>
                </>
              )}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            data-testid="stem-pack-share-btn"
            onClick={handleShare}
            title="Copy pack link — anyone can preview, remix or download from the pack page"
            className="rounded-lg p-1.5 text-slate-500 transition hover:bg-cyan-500/10 hover:text-cyan-300"
          >
            <Share2 className="h-4 w-4" />
          </button>
          {pack.is_owner && (
            <button
              type="button"
              data-testid="stem-pack-delete-btn"
              onClick={handleDelete}
              disabled={busy === "delete"}
              title="Delete this pack"
              className="rounded-lg p-1.5 text-slate-500 transition hover:bg-rose-500/10 hover:text-rose-300 disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </header>

      {/* Description — only if present, capped to 2 lines */}
      {pack.description && (
        <p className="line-clamp-2 text-sm text-slate-300/80">{pack.description}</p>
      )}

      {/* Stem chips */}
      <div className="flex flex-wrap gap-1.5">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-[11px] font-medium text-emerald-200 ring-1 ring-emerald-400/30">
          <Music2 className="h-3 w-3" />
          {pack.stem_count} stem{pack.stem_count === 1 ? "" : "s"}
        </span>
        {tempo && (
          <span className="rounded-full bg-fuchsia-500/10 px-2.5 py-0.5 text-[11px] font-medium text-fuchsia-200 ring-1 ring-fuchsia-400/30">
            {tempo}
          </span>
        )}
        {keyLabel && (
          <span className="rounded-full bg-cyan-500/10 px-2.5 py-0.5 text-[11px] font-medium text-cyan-200 ring-1 ring-cyan-400/30">
            Key · {keyLabel}
          </span>
        )}
        {pack.genre && pack.genre !== "other" && (
          <span className="rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-medium uppercase tracking-wide text-amber-200 ring-1 ring-amber-400/30">
            {pack.genre}
          </span>
        )}
      </div>

      {/* Tags */}
      {pack.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {pack.tags.slice(0, 6).map((t) => (
            <span
              key={t}
              className="rounded-md bg-white/5 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-400"
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* Footer — stats row + actions row */}
      <footer className="mt-1 border-t border-white/5 pt-3">
        <div className="mb-2.5 flex items-center justify-between gap-2 text-[11px] text-slate-500">
          <button
            type="button"
            data-testid="stem-pack-like-btn"
            onClick={handleLike}
            disabled={busy === "like"}
            className={
              "inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium transition disabled:opacity-50 " +
              (pack.is_liked
                ? "bg-fuchsia-500/20 text-fuchsia-200 ring-1 ring-fuchsia-400/40 hover:bg-fuchsia-500/30"
                : "text-slate-400 hover:bg-fuchsia-500/10 hover:text-fuchsia-200")
            }
          >
            <Heart className={`h-3.5 w-3.5 ${pack.is_liked ? "fill-current" : ""}`} />
            <span data-testid="stem-pack-like-count">{pack.like_count || 0}</span>
          </button>
          <span className="flex items-center gap-2 text-slate-500">
            <span data-testid="stem-pack-remix-count">
              <b className="text-slate-300">{pack.remix_count || 0}</b> remix{(pack.remix_count || 0) === 1 ? "" : "es"}
            </span>
            <span className="text-slate-700">·</span>
            <span data-testid="stem-pack-download-count">
              <b className="text-slate-300">{pack.download_count || 0}</b> dl
            </span>
          </span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            data-testid="stem-pack-remix-btn"
            onClick={handleRemix}
            disabled={busy === "remix"}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-fuchsia-500 to-violet-500 px-3 py-2 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-50"
          >
            <Wand2 className="h-3.5 w-3.5" />
            {busy === "remix" ? "Loading…" : "Remix"}
          </button>
          <button
            type="button"
            data-testid="stem-pack-download-btn"
            onClick={handleDownload}
            disabled={busy === "download"}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-emerald-400/40 bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100 transition hover:bg-emerald-500/20 hover:text-emerald-50 disabled:opacity-50"
          >
            <Download className="h-3.5 w-3.5" />
            {busy === "download" ? "Packing…" : "Download"}
          </button>
        </div>
      </footer>

      {/* iter-263 — Preview progress strip. Anchored to the bottom edge
          of the card; only renders when this card owns the player. */}
      {preview.isMine && preview.status !== "idle" && (
        <div
          data-testid="stem-pack-preview-progress"
          className="absolute inset-x-0 bottom-0 h-1 overflow-hidden bg-white/5"
          aria-hidden="true"
        >
          <div
            className={
              "h-full transition-[width] duration-150 ease-linear " +
              (preview.status === "error"
                ? "bg-rose-400"
                : "bg-gradient-to-r from-emerald-400 via-cyan-300 to-emerald-400")
            }
            style={{ width: `${Math.max(2, preview.progress * 100)}%` }}
          />
        </div>
      )}
    </article>
  );
}
